import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useLocation } from 'react-router-dom';
import {
    Channel,
    EditLocalSEOInfoServer,
    EmailContentItem,
    License,
    LocalSEOInfo,
    Prospect,
    ServerMeta,
} from 'types/common';
import { TCurrentUser, TPractice } from 'types/group';
import { EPPAStep } from 'types/ppaOnboarding';
import { ENV_VAR, OPS_ENV, PermissionModuleKeys } from 'constants/access';
import AccessDeniedPage from 'pages/AccessManagement/AccessDenied';
import { PracitceAddressForm } from 'types/practiceFront';
import { ERoleCode } from 'types/enumItems';
import { SettingEnum } from 'types/enumerationData';
import { ITimePair } from 'types/appointmentTime';
import { getUserId } from './localstore';
import { SHOW_KLARITY_ADVERTISING_ICON, BillingStatusTipsMapping, MODE } from 'constants/common';
import { dontShowAdvertisingPaht } from 'constants/path';
import { TSubscriptionType, TSubscriptionItemFromServer } from 'types/subscription';
import { EBillingStatus, EBillingStatusLabel, EChargeType, EChargeTypeLabel, HomeInfo } from 'types/provider';
import { getCurrentPlatformType, getViewMode } from 'utils/sessionStorage';
import { ERegisterType } from 'api/public';

export const isMobile = () => {
    const check = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && window.screen.width <= 600;
    return !!check;
};

export const isLocalhost = () => {
    const check =
        window.location.hostname.includes('localhost') ||
        window.location.hostname.includes('127.0.0.1') ||
        window.location.hostname.includes('.168');
    return !!check;
};

export const isProductionEnv = () => {
    const check =
        !isLocalhost() &&
        !window.location.hostname.includes('dev.') &&
        !window.location.hostname.includes('test.') &&
        !window.location.hostname.includes('pre.');

    return !!check;
};

export const isPrereleaseEnv = () => {
    const check = window.location.hostname.includes('pre');

    return !!check;
};

/** 是否为正式/预发布环境（非 localhost / dev / test），用于支付等需区分环境的配置 */
export const isLive = () => isProductionEnv() || isPrereleaseEnv();

export const isTestEnv = () => {
    const check = window.location.hostname.includes('test.');

    return !!check;
};

export const isDevEnv = () => {
    const check = window.location.hostname.includes('dev.') || isLocalhost();

    return !!check;
};

export const formatDate = (date?: dayjs.Dayjs, format?: string) => {
    if (!date) {
        return '';
    }
    return date.format(format || 'YYYY-MM-DD');
};

export const formatUtcDate = (date?: dayjs.Dayjs) => {
    if (!date) {
        return '';
    }
    return date.utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
};

export const getFileNameFromUrl = (url?: string) => {
    if (!url || typeof url !== 'string') {
        return '';
    }
    return url?.substring(url.lastIndexOf('/') + 1);
};

export const jumpToFormError = (dom?: HTMLElement) => {
    setTimeout(() => {
        const errors: any = document.getElementsByClassName('ant-form-item-explain-error');
        const target: any = errors?.[0]?.offsetParent;
        if (target) {
            const useDom = dom && dom.scroll;
            if (useDom) {
                dom.scroll({
                    top: target ? target.offsetTop + 100 : 300,
                    behavior: 'smooth',
                });
                return;
            }
            window.scroll({
                top: target ? target.offsetTop + 100 : 300,
                behavior: 'smooth',
            });
        }
    }, 500);
};

type GetEditableStatus = {
    allowEditOnBoarding?: boolean;
    allowEditChannel?: boolean;
};

export const getNewEditableStatus = (props: {
    currentEditable?: string;
    allowEditOnBoarding?: boolean;
    allowEditChannel?: boolean;
}): string => {
    const { currentEditable, allowEditOnBoarding, allowEditChannel } = props;
    const currentEditableInt = currentEditable ? parseInt(currentEditable, 10) : 0;
    let currentAllowEditOnBoarding = (currentEditableInt & 1) > 0;
    let currentAllowEditallowEditChannel = (currentEditableInt & 2) > 0;
    if (allowEditOnBoarding !== undefined) {
        currentAllowEditOnBoarding = allowEditOnBoarding;
    }
    if (allowEditChannel !== undefined) {
        currentAllowEditallowEditChannel = allowEditChannel;
    }
    let ret = 0;
    if (currentAllowEditOnBoarding) {
        ret |= 1;
    }
    if (currentAllowEditallowEditChannel) {
        ret |= 2;
    }
    return `${ret}`;
};

export const getEditableStatus = (currentEditable?: string): GetEditableStatus => {
    const currentEditableInt = currentEditable ? parseInt(currentEditable, 10) : 0;
    const currentAllowEditOnBoarding = (currentEditableInt & 1) > 0;
    const currentAllowEditallowEditChannel = (currentEditableInt & 2) > 0;
    return {
        allowEditOnBoarding: currentAllowEditOnBoarding,
        allowEditChannel: currentAllowEditallowEditChannel,
    };
};

export const getBrowserTimezone = (): string => {
    return new window.Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getBrowserTimezoneOffset = (): string => {
    const minOff = new Date().getTimezoneOffset();
    const hour = -minOff / 60;
    if (hour > 0) {
        if (hour < 10) {
            return `+0${hour}`;
        }
        return `+${hour}`;
    }
    if (hour > -10) {
        return `-0${Math.abs(hour)}`;
    }
    return `-${Math.abs(hour)}`;
};

export const useQuery = () => {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
};

// export const removeHtmlTag = (str: string = '') => str?.replace(/<.*?>/g, '')?.replace(/\r|\n|(\r\n)/g, '') || '';
export const removeHtmlTag = (str: string = '') => str?.replace(/<[^>]+>|[\r\n]/g, '') || '';
export const removeHtmlTagButWrap = (str: string = '') => str?.replace(/<.*?>/g, '') || '';

export const isOpPortal = () => {
    if (getViewMode() === MODE.OPS_PREVIEW) {
        return false;
    }
    return !!window.location.hostname.includes('internal.boxpractice') || localStorage.getItem(ENV_VAR) === OPS_ENV;
};

export const getUrlParam = (key: string): string | undefined => {
    const query = window.location.search.substring(1);
    const vars = query.split('&');
    for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split('=');
        if (pair[0] === key) {
            return decodeURIComponent(pair[1]);
        }
    }
    return undefined;
};

export const insertUrlParam = (key: string, value: string) => {
    if (!value) {
        return;
    }
    if (window.history.replaceState) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set(key, value);
        const newurl = `${window.location.protocol}//${window.location.host}${
            window.location.pathname
        }?${searchParams.toString()}`;
        window.history.replaceState({ path: newurl }, '', newurl);
    }
};

export const removeUrlParam = (key: string) => {
    if (window.history.replaceState) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(key);
        let newurl = `${window.location.protocol}//${window.location.host}${
            window.location.pathname
        }?${searchParams.toString()}`;
        if (searchParams.keys.length === 0) {
            newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
        }
        window.history.replaceState({ path: newurl }, '', newurl);
    }
};

export const getStrWithLineBreaker = (str: string) => {
    return str?.split('\n').map((s) => <p>{s}</p>);
};

export const stripHtmlTags = (str: string) => {
    if (!str) return str;
    return str.replace(/(<([^>]+)>)/gi, '');
};

export const getAgeFromBirthday = (date?: string): number | undefined => {
    if (!date) {
        return undefined;
    }
    const current = dayjs();
    const birthday = dayjs(date);
    const yearsDiff = current.diff(birthday, 'year');
    const monthsDiff = current.diff(birthday, 'month');
    const daysDiff = current.diff(birthday, 'day');
    if (monthsDiff < 0 || (monthsDiff === 0 && daysDiff < 0)) {
        return yearsDiff - 1;
    }
    return yearsDiff;
};

export const isLicenseExpired = (license: License): boolean => {
    const { expireDate } = license;
    if (!expireDate) {
        return false;
    }
    return dayjs().valueOf() - dayjs(expireDate).valueOf() > 0;
};

export const onlyOneFieldHasValue = (params: Array<string>) => {
    let sum = 0;
    for (let i = 0; i < params.length; i++) {
        sum += Number(!!params[i]);
    }
    return sum === 1;
};

export const optimizeSnippet = (snippet: string): string => {
    if (!snippet) {
        return snippet;
    }
    const inx = snippet.indexOf('If you have any further que');
    if (inx > -1) {
        return snippet.substring(0, inx);
    }
    return snippet;
};

export const getEmailDisplay = (email: EmailContentItem): string => {
    if (!email) {
        return '';
    }
    const contentWithHtml = email.content || '';
    const regex = /\s{3,}/g;
    const regexHtml = /<("[^"]*"|'[^']*'|[^'">])*>/;
    const isContentHasHtml = regexHtml.test(email.content);
    const contentWithoutHtml = email.body || '';
    //contentWithoutHtml = email.body || '' //new add
    //const { snippet } = email;
    // if (contentWithHtml.indexOf('<div style="margin-top: 16px;font-weight:500">\n') > 0) {
    //     //email template from practice front
    //     const content = contentWithHtml.substring(contentWithHtml.indexOf('<div style="margin-top: 16px;font-weight:500">\n'));
    //     const endPoint = content.indexOf('</div>\n ');
    //     if (endPoint > 0) {
    //         return content.substring(0, endPoint).replace('<div style="margin-top: 16px;font-weight:500">\n', '');
    //     }
    // }
    // if (contentWithHtml.indexOf('Message Content:') > 0 && contentWithHtml.indexOf('Please note that this e') > 0) {
    //     //email template from practice front
    //     const content = contentWithHtml.substring(contentWithHtml.indexOf('Message Content:'), contentWithHtml.indexOf('Please note that this e'));
    //     return content.replace('Message Content:', '').replace(/\*/g, '').replace('\r\n', '');
    // }
    if (
        contentWithHtml.includes('hesitate to contact me by responding to this email') &&
        contentWithHtml.startsWith(
            '<!DOCTYPE html>\n<html lang="en_US" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml"\n',
        )
    ) {
        //email from replay
        const content = contentWithHtml.substring(
            contentWithHtml.indexOf('<div style="margin-top: 16px;font-weight:500;text-align: left">'),
        );
        const endPoint = content.indexOf('</div>');
        if (endPoint > 0) {
            return content
                .substring(0, endPoint)
                .replace('<div style="margin-top: 16px;font-weight:500;text-align: left">', '')
                .trim();
        }
    }
    // contentWithoutHtml = contentWithoutHtml
    //     ?.replace(
    //         /Hello,.+?\r\nYou've received a new message from .+?\r\n(.+?)/,
    //         '$1',
    //     )
    //     .replace(
    //         /If you have any further questions or require additional assistance, please(?:\s*\r?\n)?don't hesitate to contact me by responding to this email(?:\.\r?\n)?/,
    //         '',
    //     );

    return contentWithoutHtml
        ?.trim()
        ?.replace(/([\r\n]+)([A-Z])/g, '<br />$2')
        .replace(regex, '<br />');
};

export const displayItem = (loading: boolean, emptyText: string, text?: string | number): string | number => {
    if (loading) return '';
    if (!text) {
        return emptyText;
    }
    return text;
};

export const generateColor = (color: string, basic: number = 100) => {
    const num = parseInt(color.replace('#', ''), 16) - basic;

    return `#${num.toString(16)}`;
};

export const getImageType = (url: string) => {
    return url ? url.substring(url.lastIndexOf('.') + 1).toLowerCase() : null;
};

export const removeSpecialCharsFromStartAndEnd = (str: string = '') =>
    str.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '');

export const getElementWithTargetRole = (params: {
    access?: Record<PermissionModuleKeys, boolean> | null;
    element: React.JSX.Element;
    targetRoles?: PermissionModuleKeys[];
}): React.JSX.Element | null => {
    const { access, targetRoles, element } = params;
    if (!access) {
        return null;
    }
    if (!targetRoles || targetRoles.length === 0) {
        return element;
    }
    let ret = <AccessDeniedPage />;
    for (let i = 0; i < targetRoles?.length; i++) {
        if (access[targetRoles[i]]) {
            ret = element;
            break;
        }
    }
    return ret;
};

export const convertLinksToAnchorTags = (text: string): string => {
    if (!text) {
        return text;
    }
    // 正则表达式以匹配超链接
    const linkRegex = /(?:https?|ftp):\/\/[^\s/$.?#].[^\s]*/gi;

    // 使用replace方法将匹配的超链接替换为包含<a>标签的超链接
    const newText = text.replace(linkRegex, (match) => {
        return `<a href="${match}" target="_blank">${match}</a>`;
    });

    return newText;
};

export const transAddressDataToServiceOptions = (
    addressList: Array<PracitceAddressForm> = [],
): Array<{ value: number; label: string; isDefault: boolean }> => {
    return (addressList || []).map((e) => {
        const { id, practiceAddress, practiceCity, practiceState, practiceZip, isDefault } = e;
        return {
            value: id as number,
            label: `${practiceAddress}, ${practiceCity}, ${practiceState} ${practiceZip}`,
            isDefault,
        };
    });
};

export const getStateDisplayName = (stateCode?: string, stateList?: SettingEnum[]): string | undefined => {
    const state = stateList?.find((e) => e.dictKey?.toLowerCase() === stateCode?.toLowerCase());
    return state?.content || stateCode;
};

export const getLocalSEOInfoFromServerData = (serverData?: Record<string, EditLocalSEOInfoServer>): LocalSEOInfo[] => {
    const serverSeoInfoArray: LocalSEOInfo[] = [];
    if (serverData) {
        Object.keys(serverData).forEach((state) => {
            const data = serverData[state];
            let landmarks;
            if (data?.landmark) {
                try {
                    landmarks = JSON.parse(data.landmark);
                } catch (e) {
                    console.error(e);
                }
            }
            let cities;
            if (data?.city) {
                try {
                    cities = JSON.parse(data.city);
                } catch (e) {
                    console.error(e);
                }
            }
            let counties;
            if (data?.country) {
                try {
                    counties = JSON.parse(data.country);
                } catch (e) {
                    console.error(e);
                }
            }
            let zip;
            if (data?.zip) {
                try {
                    zip = JSON.parse(data.zip);
                } catch (e) {
                    console.error(e);
                }
            }
            serverSeoInfoArray.push({
                state,
                landmarks,
                cities,
                counties,
                zip,
            });
        });
    }
    return serverSeoInfoArray;
};

export const isSameProspect = (prospect1: Partial<Prospect>, prospect2: Partial<Prospect>) => {
    return (
        prospect1.channelId === prospect2.channelId &&
        prospect1.firstName === prospect2.firstName &&
        prospect1.lastName === prospect2.lastName &&
        prospect1.tel === prospect2.tel
    );
};

export const urlSearchParamsReplacement = (url: string, key: string, value: string) => {
    let newLink = url;
    if (url && key) {
        const urlObj = new URL(url);

        const { search, origin, pathname } = urlObj;
        const searchParams = new URLSearchParams(search);

        searchParams.set(key, value || '');

        newLink = `${origin}${pathname}?${searchParams.toString()}`;
    }

    return newLink;
};

export const maskEmail = (email: string, repeat?: number) => {
    if (!email.includes('@')) {
        return email;
    }

    const parts = email.split('@');
    const username = parts[0];
    const maskedUsername =
        username.length > 2
            ? username.charAt(0) + '*'.repeat(repeat || username.length - 2) + username.charAt(username.length - 1)
            : username;

    return `${maskedUsername}@${parts[1]}`;
};

export const sanitizeHtml = (html: string) => {
    const temp = document.createElement('div');
    // 将 HTML 字符串作为文本内容插入到 div 元素中
    temp.textContent = html;
    // 返回过滤后的 HTML 内容
    return temp.innerHTML;
};

export const decodeHtmlEntities = (htmlString: string) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = htmlString;
    return textarea.value;
};

export const loadSalesPresentationUrlInIframe = (url?: string) => {
    if (!url) {
        return;
    }

    let newUrl = url;

    // 检查 URL 是否来自 Google Slides
    if (url.startsWith('https://docs.google.com/presentation/')) {
        // 查看 URL 是否包含 '/pub'
        if (url.includes('/pub')) {
            // 将 '/pub' 替换成 '/embed'
            newUrl = url.replace('/pub', '/embed');
        }

        if (url.includes('/edit')) {
            // 将 '/pub' 替换成 '/embed'
            newUrl = url.replace('/edit', '/embed');
        }
    }

    return newUrl;
};

export const generateUrlSearch = (params: Record<string, any>) => {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value);
        }
    }

    return searchParams.toString();
};

//key may dickey or content, ignore case
export const findTargetMetaFromListByKey = (key?: string, list?: ServerMeta[]): ServerMeta | undefined => {
    if (!key || typeof key !== 'string') {
        return undefined;
    }
    return list?.find((item) => {
        return item.content.toLowerCase() === key.toLowerCase() || item.dictKey.toLowerCase() === key.toLowerCase();
    });
};

export const getDayKey = (val: Dayjs) => {
    return val.format('YYYY-MM-DD');
};

export const removeHMSInUTCTime = (utcString: string) => {
    if (!utcString) {
        return undefined;
    }

    return utcString.split('T')[0];
};

export const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

export const getAiUserId = () => {
    return `ai-${getUserId()}`;
};

export const getAiApiData = (result: any) => {
    return result?.data?.data as any;
};

export const isAiApiSuccess = (result: any) => {
    return !result.error && result?.data?.message === 'Success';
};

export const getKiwiHealthHost = (): string => {
    let host = 'https://www.kiwihealth.com';
    if (isTestEnv()) {
        host = 'https://dev-kiwihealth.boxpractice.com';
    }
    if (isDevEnv()) {
        host = 'https://dev-kiwihealth.boxpractice.com';
    }
    return host;
};

export const getKlarityProviderHost = (): string => {
    let host = 'https://provider.helloklarity.com';
    if (isPrereleaseEnv()) {
        host = 'https://prerelease-provider.helloklarity.com';
    }
    if (isTestEnv()) {
        host = 'https://klarity-test.boxpractice.com';
    }
    if (isDevEnv()) {
        host = 'https://klarity-dev.boxpractice.com';
    }
    return host;
};

export const getHelloKlarityHost = (): string => {
    let host = 'https://www.helloklarity.com';
    if (isTestEnv()) {
        host = 'https://helloklarity.klarity-test.boxpractice.com';
    }
    if (isDevEnv()) {
        host = 'https://helloklarity.klarity-dev.boxpractice.com';
    }
    return host;
};

export const getBPHost = (): string => {
    let host = 'https://provider.helloklarity.com';
    if (isTestEnv()) {
        host = 'https://klarity-test.boxpractice.com';
    }
    if (isDevEnv()) {
        host = 'https://klarity-dev.boxpractice.com';
    }
    return host;
};

export const transTimeStringToNumber = (time: string = '') => {
    const [hour, minute] = time.split(':').map(Number);

    return hour * 60 + minute;
};

export const getTheClosestTimeObj = (time: string, times: ITimePair[]): ITimePair | null => {
    const timeValue = transTimeStringToNumber(time);

    let closest = null;
    let minDiff = Infinity;

    times.forEach((t) => {
        const startValue = transTimeStringToNumber(t.start);
        const diff = Math.abs(startValue - timeValue);

        // startValue > timeBValue && startValue - timeBValue < minDiff
        if (startValue > timeValue && diff < minDiff && time !== t.start) {
            minDiff = diff;
            closest = t;
        }
    });

    return closest;
};

export const isTimeOverlap = (periodA: ITimePair, periodB: ITimePair) => {
    const startA = transTimeStringToNumber(periodA.start);
    const endA = transTimeStringToNumber(periodA.end);
    const startB = transTimeStringToNumber(periodB.start);
    const endB = transTimeStringToNumber(periodB.end);

    return startA < endB && endA > startB;
};

export const get2Decimal = (input: any) => {
    // 确保输入是数字类型
    const num = parseFloat(input);

    // 如果解析失败，返回 'NaN'
    if (Number.isNaN(num)) {
        return 0;
    }

    // 使用 toFixed 保留两位小数
    return num.toFixed(2);
};

export const shouldShowAdvertisingIcon = (currentPlan?: TSubscriptionItemFromServer) => {
    const isOPS = isOpPortal();
    if (isOPS || !currentPlan) {
        return false;
    }

    const showAdvertisingIconLS = localStorage.getItem(SHOW_KLARITY_ADVERTISING_ICON);
    return (
        showAdvertisingIconLS === '1' &&
        !dontShowAdvertisingPaht.includes(window?.location?.pathname) &&
        (currentPlan.type === TSubscriptionType.KLARITY_STARTER || currentPlan.type === TSubscriptionType.KLARITY)
    );
};

export const checkChannelIsLocked = (channel: Channel, myPlanLevel: number, isOperation?: boolean) => {
    return isOperation ? false : myPlanLevel < channel.klarityPlanLevel;
};

export const isHeadwayWebsite = (url?: string) => {
    if (!url) {
        return false;
    }
    try {
        const urlObj = new URL(url);
        const { host } = urlObj;

        if (host.replace(/www\./g, '') === 'headway.co') {
            return true;
        }
    } catch (e) {
        console.error(e);
    }

    return false;
};

export const convertImageToTransparentPNG = async (imageSrc: string, bgColor = { r: 255, g: 255, b: 255 }) => {
    if (!imageSrc) {
        return null;
    }
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // 允许跨域加载图片
        img.src = imageSrc;

        img.onload = () => {
            // 创建与图片相同大小的 Canvas
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');

            // 将图片绘制到 Canvas 上
            ctx.drawImage(img, 0, 0);

            // 获取所有像素数据
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const { data } = imageData;

            // 遍历每个像素，如果是背景颜色则将其设为透明
            for (let i = 0; i < data.length; i += 4) {
                const [r, g, b] = [data[i], data[i + 1], data[i + 2]];

                // 如果当前像素是背景颜色，将 alpha 设为 0（透明）
                if (r === bgColor.r && g === bgColor.g && b === bgColor.b) {
                    data[i + 3] = 0; // alpha 通道设为 0
                }
            }

            // 更新 Canvas 的图像数据
            ctx.putImageData(imageData, 0, 0);

            // 导出透明背景的 PNG
            canvas.toBlob((blob) => {
                if (blob) {
                    const newImgURL = URL.createObjectURL(blob);
                    resolve(newImgURL);
                } else {
                    reject(new Error('Failed to create PNG'));
                }
            }, 'image/png');
        };

        img.onerror = reject;
    });
};
export const getPlainTextLength = (content: string = '') => {
    const plainText = content.replace(/<[^>]*>/g, ''); // 移除 HTML 标签
    return plainText.length;
};

export const replaceCurlyBracesAndContent = (input?: string, value?: string) => {
    if (!input) {
        return '';
    }
    return input.replace(/\{.*?\}/g, value || '');
};

export const getRandomString = (length = 8) => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

/**
 * 构建 UTM 对象用于 API 请求
 * 从 URL 参数中获取 UTM 信息并转换为 camelCase 格式
 * @returns UTM 对象或 null（如果没有任何 UTM 参数）
 */
export const buildUtmObject = () => {
    const source = getUrlParam('utm_source');
    const medium = getUrlParam('utm_medium');
    const campaign = getUrlParam('utm_campaign');
    const term = getUrlParam('utm_term');
    const content = getUrlParam('utm_content');

    const hasAnyUtm = !!(source || medium || campaign || term || content);

    if (!hasAnyUtm) {
        return null;
    }

    return {
        utmSource: source || '',
        utmMedium: medium || '',
        utmCampaign: campaign || '',
        utmTerm: term || '',
        utmContent: content || '',
    };
};

export const checkIsRegularUser = (signUpDate?: string) => {
    // 在2024.11.01之前注册的用户为regular用户，2024.11.01为utc时间
    if (!signUpDate) {
        return false;
    }
    const signUpDateObj = new Date(signUpDate);
    const cutoffDate = new Date('2024-11-01T00:00:00Z'); // UTC时间2024.11.01
    return signUpDateObj.getTime() < cutoffDate.getTime();
};

export const generateKiwiReviewLink = (uidPublic?: string) => {
    // if (isLocalhost()) {
    //     return `http://localhost:3008/survey?uid=${uidPublic}`;
    // }
    // if (!uidPublic) {
    //     return '';
    // }
    return `${getKiwiHealthHost()}/survey?uid=${uidPublic}`;
};

export const generateKlarityReviewLink = (uidPublic?: string) => {
    // if (isLocalhost()) {
    //     return `http://localhost:3010/review-provider?uid=${uidPublic}`;
    // }
    // if (!uidPublic) {
    //     return '';
    // }
    return `${getHelloKlarityHost()}/review-provider?uid=${uidPublic}`;
};

export const getFullName = (firstName?: string, lastName?: string) => {
    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    }
    return firstName || lastName || 'Anonymous';
};

export const getPPAChannelId = () => {
    if (isProductionEnv() || isPrereleaseEnv()) {
        return '1000000139';
    }
    if (isTestEnv()) {
        return '1000000095';
    }
    return '1000000024';
};

export const STRIPE_KEY =
    isProductionEnv() || isPrereleaseEnv()
        ? 'pk_live_51MgtXaLWCWKPYTPjUOrSfOAEWGvjkqQKbczP6x2QAIfAD67w1emhH9w0J4QRhOCAZ0j1owRJFZRJVPvPa68gYd4M00GlbUbLco'
        : 'pk_test_51MgtXaLWCWKPYTPjHlPhj8n9wXZ1a43RqravxqWcSVaRHcULwjYvP1i8Y1P6AUf6qboRoVTVBxtT31OzaLEoXu5S009KRfIY8t';

/**
 * 1. 请写一个function，它的参数data，是一个数组，数组的数据结构是
 * [
    {
        dayKey: "2025-03-01",
        timeSlots: [1741528800000, 1741529700000, 1741530600000]
    }
 * ]
 * 2. function的返回值的数据结构是
 * [
    {
        dayKey: "2025-03-01",
        slotsGroup: {
        earlyMorning: [1741528800000, 1741529700000, 1741530600000],
        morning: [1741532400000, 1741533300000, 1741534200000],
        afternoon: [1741546800000, 1741547700000, 1741548600000],
        evening: [1741561200000, 1741562100000, 1741563000000]
    }]

 * 4. 将timeSlots分类, 分类规则如下：
 * a. Early Morning (0 AM - 8 AM)
    b. Morning (8 AM - 12 PM)
    c. Afternoon (12 PM - 5 PM)
    d. Evening (5 PM - 0 AM)
 * */
export const classifyTimeSlots = (data?: { dayKey: string; timeSlots: number[] }[]) => {
    if (!data?.length) {
        return [];
    }

    return data.map((item) => {
        const { dayKey, timeSlots } = item;
        const earlyMorning = timeSlots.filter((time) => {
            const date = new Date(time);
            const hour = date.getHours();
            return hour >= 0 && hour < 8;
        });
        const morning = timeSlots.filter((time) => {
            const date = new Date(time);
            const hour = date.getHours();
            return hour >= 8 && hour < 12;
        });
        const afternoon = timeSlots.filter((time) => {
            const date = new Date(time);
            const hour = date.getHours();
            return hour >= 12 && hour < 17;
        });
        const evening = timeSlots.filter((time) => {
            const date = new Date(time);
            const hour = date.getHours();
            return hour >= 17 && hour <= 23;
        });

        return {
            dayKey,
            hasSlot: timeSlots && timeSlots.length > 0,
            slotsGroup: {
                earlyMorning,
                morning,
                afternoon,
                evening,
            },
        };
    });
};

export const getBillingStatusLabel = (status?: EBillingStatus) => {
    if (!status) {
        return '';
    }
    const statusLabels = {
        [EBillingStatus.DISPUTE_OPEN]: EBillingStatusLabel.DISPUTE_OPEN,
        // [EBillingStatus.DISPUTE_CLOSED]: EBillingStatusLabel.DISPUTE_CLOSED,
        [EBillingStatus.PENDING]: EBillingStatusLabel.PENDING,
        // [EBillingStatus.PAID]: EBillingStatusLabel.PAID,
        [EBillingStatus.FAILED]: EBillingStatusLabel.FAILED,
        [EBillingStatus.CANCELED]: EBillingStatusLabel.CANCELED,
        [EBillingStatus.BILLING_CANCELED]: EBillingStatusLabel.CANCELED,
        // [EBillingStatus.NOT_READY]: EBillingStatusLabel.NOT_READY,
        [EBillingStatus.PROCESSING]: EBillingStatusLabel.PROCESSING,
        [EBillingStatus.SUCCEEDED]: EBillingStatusLabel.SUCCEEDED,
        [EBillingStatus.REFUND_PROCESSING]: EBillingStatusLabel.REFUND_PROCESSING,
        [EBillingStatus.REFUNDED]: EBillingStatusLabel.REFUNDED,
        [EBillingStatus.REFUND_FAILED]: EBillingStatusLabel.REFUND_FAILED,
        [EBillingStatus.DISPUTE_PROCESSING]: EBillingStatusLabel.DISPUTE_PROCESSING,
        [EBillingStatus.DISPUTE_WON]: EBillingStatusLabel.DISPUTE_WON,
        [EBillingStatus.DISPUTE_LOST]: EBillingStatusLabel.DISPUTE_LOST,
        [EBillingStatus.PATIENT_NOT_READY]: EBillingStatusLabel.PATIENT_NOT_READY,
        [EBillingStatus.PROVIDER_NOT_READY]: EBillingStatusLabel.PROVIDER_NOT_READY,
        [EBillingStatus.NOT_READY]: EBillingStatusLabel.NOT_READY,
        [EBillingStatus.FULLY_PAID]: EBillingStatusLabel.FULLY_PAID,
        [EBillingStatus.PARTIAL_PAID]: EBillingStatusLabel.PARTIAL_PAID,
        [EBillingStatus.PARTIAL_REFUNDED]: EBillingStatusLabel.PARTIAL_REFUNDED,
        [EBillingStatus.OVERDUE]: EBillingStatusLabel.OVERDUE,
        [EBillingStatus.SCHEDULED]: EBillingStatusLabel.SCHEDULED,
        [EBillingStatus.SENT]: EBillingStatusLabel.SENT,
        [EBillingStatus.PAYMENT_FAILED]: EBillingStatusLabel.PAYMENT_FAILED,
    };
    return statusLabels[status] || status;
};

export const getBillingStatusDescription = (status?: EBillingStatus) => {
    if (!status) {
        return '';
    }
    // const statusDescriptions = {
    //     [EBillingStatus.DISPUTE_OPEN]: EBillingStatusDes.DISPUTE_OPEN,
    //     [EBillingStatus.DISPUTE_CLOSED]: EBillingStatusDes.DISPUTE_CLOSED,
    //     [EBillingStatus.PENDING]: EBillingStatusDes.PENDING,
    //     [EBillingStatus.PAID]: EBillingStatusDes.PAID,
    //     [EBillingStatus.FAILED]: EBillingStatusDes.FAILED,
    //     [EBillingStatus.CANCELED]: EBillingStatusDes.CANCELED,
    //     [EBillingStatus.NOT_READY]: EBillingStatusDes.NOT_READY,
    //     [EBillingStatus.PROCESSING]: EBillingStatusDes.PROCESSING,
    //     [EBillingStatus.SUCCEEDED]: EBillingStatusDes.SUCCEEDED,
    //     [EBillingStatus.REFUND_PROCESSING]: EBillingStatusDes.REFUND_PROCESSING,
    //     [EBillingStatus.REFUNDED]: EBillingStatusDes.REFUNDED,
    //     [EBillingStatus.REFUND_FAILED]: EBillingStatusDes.REFUND_FAILED,
    //     [EBillingStatus.DISPUTE_PROCESSING]:
    //         EBillingStatusDes.DISPUTE_PROCESSING,
    //     [EBillingStatus.DISPUTE_WON]: EBillingStatusDes.DISPUTE_WON,
    //     [EBillingStatus.DISPUTE_LOST]: EBillingStatusDes.DISPUTE_LOST,
    // };

    return BillingStatusTipsMapping[status] || status;
};

export const getChargeTypeLabel = (chargeType: EChargeType) => {
    const chargeTypeLabels = {
        [EChargeType.NO_SHOW]: EChargeTypeLabel.NO_SHOW,
        [EChargeType.LATE_CANCELLATION]: EChargeTypeLabel.LATE_CANCELLATION,
        [EChargeType.RESCHEDULE]: EChargeTypeLabel.RESCHEDULE,
        [EChargeType.LEAD_GEN_FEE]: EChargeTypeLabel.LEAD_GEN_FEE,
        [EChargeType.PARTIAL_PAYMENT]: EChargeTypeLabel.PARTIAL_PAYMENT,
        [EChargeType.FULL_SELF_PAYMENT]: EChargeTypeLabel.FULL_SELF_PAYMENT,
        [EChargeType.CO_PAY]: EChargeTypeLabel.CO_PAY,
        [EChargeType.OTHER]: EChargeTypeLabel.OTHER,
        [EChargeType.TEXT_VISIT]: EChargeTypeLabel.TEXT_VISIT,
        [EChargeType.INVOICE]: EChargeTypeLabel.INVOICE,
        [EChargeType.PPA_BOOKING_DEPOSIT]: EChargeTypeLabel.PPA_BOOKING_DEPOSIT,
        [EChargeType.PPA_BOOKING_BALANCE]: EChargeTypeLabel.PPA_BOOKING_BALANCE,
    };
    return chargeTypeLabels[chargeType] || chargeType;
};

export const getPPAHelloKlarityHost = () => {
    if (isLocalhost()) {
        // return 'http://localhost:3010';
        return 'https://dev.d2ysjydukc4efk.amplifyapp.com/';
        // return 'https://test.d2ysjydukc4efk.amplifyapp.com/';
    }

    if (isProductionEnv() || isPrereleaseEnv()) {
        return 'https://helloklarity.com';
    }

    if (isTestEnv()) {
        return 'https://test.d2ysjydukc4efk.amplifyapp.com/';
    }

    if (isDevEnv()) {
        return 'https://dev.d2ysjydukc4efk.amplifyapp.com/';
    }
};

// klarity-dev.boxpractice.com
// klarity-test.boxpractice.com
// https://provider.helloklarity.com

export const isKlarityHost = () => {
    return (
        window.location.hostname.includes('klarity-') || window.location.hostname.includes('provider.helloklarity.com')
    );
};

export const shouldUseKlarityUi = (homeInfo?: HomeInfo) => {
    if (isOpPortal()) {
        return false;
    }
    if (isLocalhost()) {
        if (getUserId() === 1446) {
            return true;
        }
        if (
            getCurrentPlatformType() === ERegisterType.KLARITY_PORTAL ||
            (homeInfo?.providerTypes?.length === 1 && homeInfo?.providerTypes[0] === 'KLARITY_PORTAL')
        ) {
            return true;
        } else {
            return false;
        }
    }
    return isKlarityHost();
};

export const getKlarityDomain = () => {
    if (isPrereleaseEnv()) {
        return 'prerelease-provider.helloklarity.com';
    }
    if (isTestEnv()) {
        return 'klarity-test.boxpractice.com';
    }
    if (isDevEnv()) {
        return 'klarity-dev.boxpractice.com';
    }
    return 'provider.helloklarity.com';
};

/**
 * 获取切换账号后的跳转域名
 * @param isKlarityAccount 是否为Klarity账号
 * @returns 跳转的域名，如果是localhost则返回null（使用reload）
 */
export const getSwitchAccountDomain = (isKlarityAccount: boolean): string | null => {
    // 如果是localhost环境，返回null，使用window.location.reload()
    if (isLocalhost()) {
        return null;
    }

    if (!isKlarityAccount) {
        return `${getKlarityDomain()}`;
    }

    if (isProductionEnv()) {
        return 'app.kiwihealth.com';
    } else if (isPrereleaseEnv()) {
        return 'preapp.kiwihealth.com';
    } else if (isTestEnv()) {
        return 'test.boxpractice.com';
    } else if (isDevEnv()) {
        return 'dev.boxpractice.com';
    }

    return 'app.kiwihealth.com';
};

export const helpNotionUrl = 'https://klarityhealth.notion.site/Klarity-Provider-FAQs-25018650acdf803abca9d028b6f11eb6';

export const platformAgreementUrl =
    'https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/agreement/platform-access-and-services-2025100901.html';
export const codeOfConductAgreementUrl =
    'https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/contract/template/html/V1.1_CODE_OF_CONDUCT.html';

export const helpLarkUrl = 'https://klarityhealth.larksuite.com/wiki/UX4IwiqhzifswbknDuxuEcQhsce?opendoc=1';

/** eFax marketing / FAQ wiki — embed with ?opendoc=1 (same pattern as Help Center). */
export const efaxFaqLarkUrl = 'https://klarityhealth.usttp.larksuite.com/wiki/FodVwCaCJiJlYkkXChzu4dG3tfd?opendoc=1';

export const formatUTCTime = (utcTime?: string, format?: string): string => {
    if (!utcTime) {
        return '-';
    }
    return dayjs
        .utc(utcTime)
        .local()
        .format(format || 'MM/DD/YYYY');
};

export const getIntakeFormUrlByTextVisitId = (textVisitId: number) => {
    return `${getHelloKlarityHost()}/consent-forms?id=${textVisitId}&type=text_visit`;
};

export const getIntakeFormUrlByAppointmentId = (appointmentId: number) => {
    return `${getHelloKlarityHost()}/consent-forms?id=${appointmentId}&type=video`;
};

export const isPreviewLoginPage = () => {
    const { pathname } = window.location;
    return pathname.includes('signin') || pathname.includes('login');
};

export const canShowPracticeGroupFuns = (currentUser?: TCurrentUser): boolean => {
    //return false;
    //return true after v2 finish
    // if (isLocalhost() || isDevEnv()) {
    //     return true;
    // }
    return true;
};

export const canShowGroupPracticeView = (currentUser?: TCurrentUser): boolean => {
    if (!canShowPracticeGroupFuns(currentUser)) {
        return false;
    }
    const role = currentUser?.roleCode as ERoleCode | undefined;
    return (
        !!role &&
        [
            ERoleCode.GROUP_OWNER,
            ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT,
            ERoleCode.GROUP_CLINICAL_ASSISTANT,
            ERoleCode.GROUP_PRACTICE_MANAGER,
        ].includes(role) &&
        !!currentUser?.groupPractice
    );
};

export const canShowGroupPracticeCalendarSelect = (currentUser?: TCurrentUser, practiceList?: TPractice[]): boolean => {
    if (!canShowPracticeGroupFuns(currentUser)) {
        return false;
    }
    const role = currentUser?.roleCode as ERoleCode | undefined;
    //current practicelist has more than 1 provider
    const currentPracticeList = practiceList?.find((item) => item.orgId === currentUser?.organizationId);
    if (!currentPracticeList?.providers?.length) {
        return false;
    }
    return (
        !!role &&
        [
            ERoleCode.GROUP_OWNER,
            ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT,
            ERoleCode.GROUP_CLINICAL_ASSISTANT,
            ERoleCode.GROUP_PRACTICE_MANAGER,
        ].includes(role) &&
        !!currentUser?.groupPractice
    );
};
export const canShowTeamManagementView = (currentUser?: TCurrentUser): boolean => {
    // const role = currentUser?.roleCode as ERoleCode | undefined;
    // return role !== ERoleCode.GROUP_PROVIDER;
    return true;
};

export const canShowPracticeSwitcher = (practiceList?: TPractice[]): boolean => {
    let ret = !!(practiceList && practiceList?.length > 1);
    if (practiceList && practiceList?.length === 1) {
        const practice = practiceList[0];
        if (practice.providers.length > 1) {
            ret = true;
        }
    }
    return ret;
};

export const canShowTeamManagementAction = (currentUser?: TCurrentUser): boolean => {
    return (
        (!!currentUser &&
            [ERoleCode.GROUP_OWNER, ERoleCode.GROUP_PRACTICE_MANAGER].includes(currentUser?.roleCode as ERoleCode)) ||
        (!!currentUser && currentUser?.isAdmin)
    );
};

export const canSkipPaymentConfirmForPrescription = (currentUser?: TCurrentUser): boolean => {
    return (
        !!currentUser &&
        [ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT, ERoleCode.GROUP_CLINICAL_ASSISTANT].includes(
            currentUser?.roleCode as ERoleCode,
        )
    );
};

export const canShowPracticeOverview = (currentUser?: TCurrentUser): boolean => {
    return (
        !!currentUser &&
        !!currentUser.groupPractice &&
        [ERoleCode.GROUP_OWNER, ERoleCode.GROUP_PRACTICE_MANAGER].includes(currentUser?.roleCode as ERoleCode)
    );
};

export const isPPAOnboardingCompleted = (homeInfo?: {
    completedOnboarding?: boolean;
    klarityOnboardingStep?: EPPAStep | string;
} | null): boolean => {
    if (!homeInfo) return false;
    return !!homeInfo.completedOnboarding || homeInfo.klarityOnboardingStep === EPPAStep.STEP_21;
};

export const isGroupAdmin = (currentUser?: TCurrentUser): boolean => {
    if (!currentUser || !currentUser.groupPractice) {
        return false;
    }
    return (
        [ERoleCode.GROUP_OWNER, ERoleCode.GROUP_PRACTICE_MANAGER].includes(currentUser?.roleCode as ERoleCode) ||
        currentUser?.isAdmin
    );
};
